import { z } from 'zod'
import { toRouterTokenAddress } from './client'
import type { RouterQuoteResponse } from './types'

// Audit #2: a 2xx quote body was previously cast straight to RouterQuoteResponse and its execution
// data (routeEnvelope, tokenIn/out, amountIn) trusted, while the UI displayed currencies/amount from
// the REQUEST. A mismatched/corrupt response could therefore show token A / amount X but carry a
// signed route for token B / amount Y. This module parses the body with a strict runtime schema
// (unknown fields rejected) AND binds every request-derived field to what the user actually asked for.

const SUPPORTED_QUOTE_SCHEMA_VERSIONS = new Set(['1.1.0'])
// Follow-up audit: allowlist the deployment version too (not just schemaVersion). The build validator
// binds build.deploymentVersion === quote.deploymentVersion, so an obsolete/unknown deployment would
// otherwise be accepted as long as the build echoes it. Update this set when 4663 redeploys.
const SUPPORTED_DEPLOYMENT_VERSIONS = new Set(['robinhood-2026-07-09-r1'])

const address = z.string().regex(/^0x[0-9a-fA-F]{40}$/)
const intString = z.string().regex(/^[0-9]+$/)

const feeSchema = z
  .object({ feeBps: z.number().int().min(0).max(10_000), feeToken: address, feeRecipient: address.nullable() })
  .strict()

const legSchema = z
  .object({
    protocol: z.literal('UNISWAP_V3'),
    pool: address,
    tokenIn: address,
    tokenOut: address,
    feeTierPips: z.number().int().positive(),
    amountIn: intString,
    amountOut: intString,
  })
  .strict()

const envelopeSchema = z
  .object({ payload: z.string().min(1), signature: z.string().min(1), keyId: z.string().min(1) })
  .strict()

const quoteSchema = z
  .object({
    schemaVersion: z.string(),
    deploymentVersion: z.string().min(1),
    routeId: z.string().regex(/^rt_/),
    chainId: z.number().int(),
    tradeType: z.literal('EXACT_INPUT'),
    buildable: z.literal(false),
    tokenIn: address,
    tokenOut: address,
    amountIn: intString,
    grossAmountOut: intString,
    feeAmount: intString,
    netAmountOut: intString,
    fee: feeSchema,
    quoterGasEstimate: intString,
    blockNumber: intString,
    createdAt: z.string(),
    expiresAt: z.string(),
    legs: z.array(legSchema).min(1),
    routeHash: z.string().regex(/^0x[0-9a-fA-F]{64}$/),
    source: z.literal('KIBA_ROUTER'),
    rankingMethod: z.string(),
    routeEnvelope: envelopeSchema,
  })
  .strict()

export interface QuoteBindRequest {
  /** As sent to the router — may be the app's native sentinel ('ETH'); normalized here for comparison. */
  tokenIn: string
  tokenOut: string
  amount: string
  chainId: number
}

export type QuoteValidationResult =
  | { ok: true; quote: RouterQuoteResponse }
  | { ok: false; reason: string }

export function validateQuoteResponse(raw: unknown, req: QuoteBindRequest): QuoteValidationResult {
  const parsed = quoteSchema.safeParse(raw)
  if (!parsed.success) {
    const issue = parsed.error.issues[0]
    return { ok: false, reason: `malformed quote: ${issue ? `${issue.path.join('.')} ${issue.message}` : 'schema mismatch'}` }
  }
  const q = parsed.data

  if (!SUPPORTED_QUOTE_SCHEMA_VERSIONS.has(q.schemaVersion)) {
    return { ok: false, reason: `unsupported schemaVersion: ${q.schemaVersion}` }
  }
  if (!SUPPORTED_DEPLOYMENT_VERSIONS.has(q.deploymentVersion)) {
    return { ok: false, reason: `unsupported deploymentVersion: ${q.deploymentVersion}` }
  }
  if (q.chainId !== req.chainId) {
    return { ok: false, reason: `chainId mismatch: expected ${req.chainId}, got ${q.chainId}` }
  }
  const wantIn = toRouterTokenAddress(req.tokenIn).toLowerCase()
  const wantOut = toRouterTokenAddress(req.tokenOut).toLowerCase()
  if (q.tokenIn.toLowerCase() !== wantIn) {
    return { ok: false, reason: 'tokenIn does not match the request' }
  }
  if (q.tokenOut.toLowerCase() !== wantOut) {
    return { ok: false, reason: 'tokenOut does not match the request' }
  }
  if (q.amountIn !== req.amount) {
    return { ok: false, reason: 'amountIn does not match the request' }
  }
  // tradeType EXACT_INPUT, source KIBA_ROUTER, and buildable=false are enforced by the schema literals.
  return { ok: true, quote: q as RouterQuoteResponse }
}
